import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Table, Card, Button, Space, Input, Switch, Select } from 'antd';
import { PlusOutlined, SearchOutlined, HolderOutlined } from '@ant-design/icons';
import { getCategoriesAdmin } from '../../graphql/queries';
import { updateCategory, reorderCategories } from '../../graphql/mutation';
import { useNavigate } from 'react-router-dom';
import { getRowLinkProps, withRowLink } from '../../utils/rowLink';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import './Categories.css';

const TABLE_STATE_KEY = 'categoriesTableState';

const readSavedTableState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(TABLE_STATE_KEY)) || {};
  } catch {
    return {};
  }
};

// Draggable Row Component
const DraggableRow = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </tr>
  );
};

const Categories = () => {
  const navigate = useNavigate();
  const [savedState] = useState(readSavedTableState);
  const [pagination, setPagination] = useState(savedState.pagination || {
    current: 1,
    pageSize: 10,
  });
  const [searchTerm, setSearchTerm] = useState(savedState.searchTerm || '');
  const [isHiddenFilter, setIsHiddenFilter] = useState(
    'isHiddenFilter' in savedState ? savedState.isHiddenFilter : false
  );
  const [sortedCategories, setSortedCategories] = useState([]);
  const pendingScrollY = useRef(savedState.scrollY);

  const { loading, data, refetch } = useQuery(getCategoriesAdmin);

  const [updateCategoryMutation] = useMutation(updateCategory);
  const [reorderCategoriesMutation] = useMutation(reorderCategories);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  useEffect(() => {
    if (data?.getCategoriesAdmin) {
      const sorted = [...data.getCategoriesAdmin].sort((a, b) => a.rowNumber - b.rowNumber);
      setSortedCategories(sorted);
    }
  }, [data]);

  // Persist table state so returning from a category detail keeps page and search
  useEffect(() => {
    try {
      sessionStorage.setItem(TABLE_STATE_KEY, JSON.stringify({ pagination, searchTerm, isHiddenFilter }));
    } catch {}
  }, [pagination, searchTerm, isHiddenFilter]);

  // Restore scroll position once the categories have rendered
  useEffect(() => {
    if (pendingScrollY.current == null || sortedCategories.length === 0) return;
    const y = pendingScrollY.current;
    pendingScrollY.current = null;
    requestAnimationFrame(() => window.scrollTo(0, y));
  }, [sortedCategories]);

  const handleRowClick = (record) => {
    try {
      sessionStorage.setItem(TABLE_STATE_KEY, JSON.stringify({
        pagination,
        searchTerm,
        isHiddenFilter,
        scrollY: window.scrollY,
      }));
    } catch {}
    navigate(`/categories/${record.id}`);
  };

  const onDragEnd = async ({ active, over }) => {
    if (active.id !== over?.id) {
      const activeIndex = sortedCategories.findIndex((i) => i.id === active.id);
      const overIndex = sortedCategories.findIndex((i) => i.id === over?.id);

      const newSortedCategories = arrayMove(sortedCategories, activeIndex, overIndex);
      setSortedCategories(newSortedCategories);

      try {
        await reorderCategoriesMutation({
          variables: { ids: newSortedCategories.map((c) => c.id) },
        });
        showSnackBar('success', 'Success', 'Category order updated successfully');
      } catch (error) {
        showSnackBar('error', 'Error', 'Failed to update category order');
        // Revert on error
        if (data?.getCategoriesAdmin) {
          const sorted = [...data.getCategoriesAdmin].sort(
            (a, b) => a.rowNumber - b.rowNumber
          );
          setSortedCategories(sorted);
        }
      }
    }
  };

  const handleIsHiddenToggle = async (record, checked) => {
    try {
      await updateCategoryMutation({
        variables: {
          categoryId: record.id,
          isHidden: checked,
        },
      });
      setSortedCategories((prev) =>
        prev.map((cat) => (cat.id === record.id ? { ...cat, isHidden: checked } : cat))
      );
      showSnackBar('success', 'Success', 'Category visibility updated');
    } catch (error) {
      showSnackBar('error', 'Error', 'Failed to update category visibility');
    }
  };

  const columns = [
    {
      key: 'sort',
      width: 50,
      align: 'center',
      render: () => <HolderOutlined style={{ cursor: 'move', color: '#999' }} />,
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '100px',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '300px',
    },
    {
      title: 'Row Number',
      dataIndex: 'rowNumber',
      key: 'rowNumber',
      width: '120px',
      sorter: (a, b) => a.rowNumber - b.rowNumber,
    },
    {
      title: 'Hidden',
      key: 'isHidden',
      width: '100px',
      align: 'center',
      render: (_, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Switch
            checked={record.isHidden}
            onChange={(checked) => handleIsHiddenToggle(record, checked)}
          />
        </div>
      ),
    },
  ];

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  // Filter data based on search term and visibility
  const filteredData = sortedCategories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (isHiddenFilter == null || Boolean(category.isHidden) === isHiddenFilter)
  );

  return (
    <div className="categories-container">
      <Card>
        <div className="page-header">
          <div className="header-content">
            <h1>Categories</h1>
            <p>Manage and organize video categories</p>
          </div>
          <Button 
            type="primary" 
            size="large"
            icon={<PlusOutlined />}
            onClick={() => navigate('/categories/create')}
          >
            Create Category
          </Button>
        </div>

        <div className="filters-section">
          <Space size="large" wrap>
            <div className="filter-item">
              <Input
                placeholder="Search categories by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: 250 }}
                prefix={<SearchOutlined />}
                allowClear
              />
            </div>
            <div className="filter-item">
              <Select
                placeholder="Visibility"
                value={isHiddenFilter}
                onChange={(value) => {
                  setIsHiddenFilter(value ?? null);
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                style={{ width: 120 }}
                allowClear
                options={[
                  { value: true, label: 'Hidden' },
                  { value: false, label: 'Visible' },
                ]}
              />
            </div>
          </Space>
        </div>
        
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={filteredData.map((category) => category.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              columns={withRowLink(columns, (record) => `/categories/${record.id}`, { exclude: ['sort', 'isHidden'] })}
              dataSource={filteredData}
              loading={loading}
              rowKey="id"
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} categories`,
              }}
              onChange={handleTableChange}
              onRow={(record) => ({
                ...getRowLinkProps(`/categories/${record.id}`, () => handleRowClick(record)),
              })}
              components={{
                body: {
                  row: DraggableRow,
                },
              }}
            />
          </SortableContext>
        </DndContext>
      </Card>
    </div>
  );
};

export default Categories; 